import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
  useTellescopeQueryClient,
} from "@tellescope/tanstack-query";
import { usePollForInboxThread, useSMSCompositionPanelSearchParams } from "../../../hooks";
import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { Link } from "../../../../../components";
import { Button, Combobox, ComboboxOption, Label } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { InboxSMSRecipientItem } from "./InboxSMSRecipientItem";
import { formatName, formatPhone, phoneNumberToString } from "@tellescope/utils";
import { InboxSMSCompositionFooter } from "./InboxSMSCompositionFooter";
import { useFromNumbers, usePhoneNumberLabel } from "../../../../../hooks/calling";

export const InboxSMSCompositionPanel = () => {
  const { t } = useTranslation("inbox");
  const [searchParams, setSearchParams] = useSMSCompositionPanelSearchParams();
  const navigate = useNavigate();
  const initialComposeToRef = useRef(searchParams.composeTo);
  const queryClient = useTellescopeQueryClient();
  const [enduserSearch, setEnduserSearch] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedEnduser, setSelectedEnduser] = useState<TellescopeModelFromName<"endusers">>();

  const preselectedEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: initialComposeToRef.current?.enduserId ?? "",
    enabled: !!initialComposeToRef.current?.enduserId && !selectedEnduser,
  });

  const resolvedSelectedEnduser = selectedEnduser ?? preselectedEnduserQuery.data;

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const fromNumbers = useFromNumbers({ preferredNumber: resolvedSelectedEnduser?.defaultFromPhone });
  const getPhoneLabel = usePhoneNumberLabel();

  const initialFromNumber =
    decodeURI(phoneNumberToString(searchParams.composeFrom?.number ?? "")) || fromNumbers.defaultFromNumber;

  const initialToNumber = decodeURI(phoneNumberToString(initialComposeToRef.current?.number ?? ""));

  // On mount, if both numbers are known, check for an existing thread and redirect to it.
  // Uses a query (not autobuild) so it only finds existing threads, never creates one.
  const existingThreadQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      mdbFilter: {
        type: "SMS",
        enduserPhoneNumber: initialToNumber,
        phoneNumber: initialFromNumber,
      },
      limit: 1,
    },
    enabled: !!initialToNumber && !!initialFromNumber,
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({ ...request, lastTimestamp }),
    select: (data) => data.pages.at(0)?.threads.at(0),
  });

  const pollForThread = usePollForInboxThread({
    maxAttempts: 3,
    initialBackoffMs: 250,
    onSuccess: () => {
      queryClient.invalidateQueries({ model: "sms_messages", method: "getSome" });
    },
    onMaxAttemptsReached: () => setIsSending(false),
  });

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: decodeURI(initialComposeToRef.current?.enduserId ?? ""),
        required: true,
      },
      toNumber: {
        type: "select",
        initialValue: initialToNumber,
        required: true,
      },
      fromNumber: {
        type: "select",
        initialValue: initialFromNumber,
        required: true,
      },
    },
    validateOn: "blur",
  });

  if (existingThreadQuery.data) {
    return <Navigate to={`../${existingThreadQuery.data.id}`} replace />;
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Loading Overlay */}
      {isSending && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="flex flex-col items-center gap-1">
              <p className="text-muted-foreground">{t("Sending...")}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-b h-[52px]">
        <h3 className="text-lg font-semibold">{t("New SMS")}</h3>
        <Link to="..">
          <Button variant="ghost" size="icon">
            <X />
          </Button>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="h-full space-y-4 flex flex-col justify-between">
          <div className="flex flex-row gap-4 w-full p-4">
            <div className="flex-1 min-w-0 space-y-2">
              <Label htmlFor="from">{t("From")}</Label>
              <Select {...form.getCustomFieldProps("fromNumber")}>
                <Select.Trigger id="from" className="h-9 w-full">
                  <Select.Value placeholder="Select phone number" />
                </Select.Trigger>
                <Select.Content>
                  {fromNumbers.options.map((number) => {
                    const label = getPhoneLabel(number);
                    return (
                      <Select.Item key={number} value={number}>
                        {label !== number ? `${label}: ${formatPhone(number)}` : formatPhone(number)}
                      </Select.Item>
                    );
                  })}
                </Select.Content>
              </Select>
            </div>

            <div className="flex-1 min-w-0 space-y-2">
              <Label htmlFor="recipient">{t("To")}</Label>
              <Combobox
                value={{
                  value: form.getFieldProps("toNumber").value,
                  label: resolvedSelectedEnduser ? formatName(resolvedSelectedEnduser) : "",
                  metadata: { enduserId: form.getFieldProps("enduserId").value },
                }}
                formatSelectedValue={({ value, label }) => (
                  <p>
                    {label}
                    <span className="ml-1 text-muted-foreground">{formatPhone(value)}</span>
                  </p>
                )}
                onChange={(newVal) => {
                  if (typeof newVal?.metadata?.enduserId === "string") {
                    form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                  } else if (!newVal?.metadata?.enduserId) {
                    form.setFieldValue("enduserId", "");
                  }
                  form.setFieldValue("toNumber", newVal?.value ?? "");
                  if (newVal?.metadata?.enduserId && newVal?.value) {
                    setSearchParams((prev) => ({
                      ...prev,
                      composeTo: {
                        ...prev.composeTo,
                        enduserId: newVal?.metadata?.enduserId ?? "",
                        number: newVal?.value ?? "",
                      },
                    }));
                    setSelectedEnduser(endusersQuery.data?.find(({ id }) => id === newVal.metadata?.enduserId));
                  }
                }}
                formatOption={(props) => (
                  <InboxSMSRecipientItem
                    {...props}
                    enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
                  />
                )}
                searchQuery={enduserSearch}
                setSearchQuery={setEnduserSearch}
                customFilter={(_, options) => options}
                options={
                  endusersQuery.data?.reduce<ComboboxOption<string>[]>((acc, enduser) => {
                    if (!enduser.phone) {
                      return acc;
                    }

                    acc.push({
                      value: enduser.phone,
                      label: formatName(enduser),
                      metadata: {
                        enduserId: enduser.id,
                      },
                    });
                    return acc;
                  }, []) ?? []
                }
              />
            </div>
          </div>
          <InboxSMSCompositionFooter
            enduserId={form.values.enduserId}
            toNumber={form.values.toNumber}
            fromNumber={form.values.fromNumber}
            onSaveDraftSuccess={() => navigate("..")}
            onSend={(sms) => {
              setIsSending(true);
              pollForThread({
                enduserPhoneNumber: sms.enduserPhoneNumber ?? form.values.toNumber,
                phoneNumber: sms.phoneNumber ?? form.values.fromNumber,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
